import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import type { PurchaseOrder, Product, Location } from "../../types";

export default function PurchaseOrdersTab() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ vendor: "", locationId: "", notes: "", expectedDate: "", items: [] as any[] });
  const [itemForm, setItemForm] = useState({ productId: "", expectedQty: "1", unitCost: "" });

  useEffect(() => { if (orgId) loadData(); }, [orgId]);

  async function loadData() {
    try {
      const [pos, prods, locs] = await Promise.all([
        apiGet<PurchaseOrder[]>(`/organizations/${orgId}/purchase-orders`),
        apiGet<Product[]>(`/organizations/${orgId}/products`),
        apiGet<Location[]>(`/organizations/${orgId}/locations`),
      ]);
      setOrders((pos as any[]).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      setProducts(prods);
      setLocations(locs);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const addItem = () => {
    const prod = products.find((p) => p.productId === itemForm.productId);
    if (!prod) return;
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        productId: prod.productId,
        productName: prod.name,
        sku: prod.sku || "",
        expectedQty: parseInt(itemForm.expectedQty) || 1,
        receivedQty: 0,
        unitCost: parseFloat(itemForm.unitCost) || prod.cost || 0,
        requiresSerialNumber: prod.requiresSerialNumber || false,
      }],
    }));
    setItemForm({ productId: "", expectedQty: "1", unitCost: "" });
  };

  const removeItem = (idx: number) => {
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.items.length === 0) return;
    const totalCost = form.items.reduce((sum: number, i: any) => sum + i.unitCost * i.expectedQty, 0);
    const poNumber = `PO-${String(orders.length + 1).padStart(4, "0")}`;
    await apiPost(`/organizations/${orgId}/purchase-orders`, {
      ...form,
      poNumber,
      totalCost,
      status: "submitted",
      createdBy: user?.id || "",
    });
    setShowModal(false);
    setForm({ vendor: "", locationId: "", notes: "", expectedDate: "", items: [] });
    await loadData();
  };

  const cancelPO = async (po: any) => {
    if (!confirm(`Cancel PO ${po.poNumber}?`)) return;
    await apiPut(`/organizations/${orgId}/purchase-orders/${po.purchaseOrderId}`, { ...po, status: "cancelled" });
    await loadData();
  };

  const getLocName = (id: string) => (locations.find((l: any) => l.locationId === id) as any)?.name || "—";

  const statusColor: Record<string, string> = {
    draft: "#6b7280", submitted: "#2563eb", partial: "#d97706", received: "#16a34a", closed: "#6b7280", cancelled: "#dc2626",
  };

  if (loading) return <div className="empty-state">Loading purchase orders...</div>;

  return (
    <div>
      <div className="org-mgmt-header">
        <h2>Purchase Orders</h2>
        <button className="btn btn-primary" style={{ width: "auto" }} onClick={() => setShowModal(true)}>+ Create PO</button>
      </div>

      {orders.length === 0 ? (
        <div className="empty-state"><p>No purchase orders yet. Create one to start receiving inventory.</p></div>
      ) : (
        <table className="data-table">
          <thead><tr><th>PO #</th><th>Vendor</th><th>Location</th><th>Items</th><th>Total</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {orders.map((po: any) => (
              <tr key={po.purchaseOrderId}>
                <td style={{ fontWeight: 600 }}>{po.poNumber}</td>
                <td>{po.vendor}</td>
                <td>{getLocName(po.locationId)}</td>
                <td>{po.items?.length || 0}</td>
                <td>${(po.totalCost || 0).toFixed(2)}</td>
                <td><span style={{ padding: "0.15rem 0.5rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 500, background: statusColor[po.status] + "20", color: statusColor[po.status] }}>{po.status}</span></td>
                <td>
                  <div className="table-actions">
                    {(po.status === "submitted" || po.status === "partial") && (
                      <button className="delete" onClick={() => cancelPO(po)}>Cancel</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px", maxHeight: "85vh", overflow: "auto" }}>
            <h3>Create Purchase Order</h3>
            <form onSubmit={handleSubmit}>
              <div className="settings-form">
                <div className="form-row">
                  <div className="form-group"><label>Vendor</label><input value={form.vendor} onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))} placeholder="Supplier name" required /></div>
                  <div className="form-group"><label>Delivery Location</label><select value={form.locationId} onChange={(e) => setForm((f) => ({ ...f, locationId: e.target.value }))} required><option value="">Select...</option>{locations.map((l: any) => <option key={l.locationId} value={l.locationId}>{l.name}</option>)}</select></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Expected Date</label><input type="date" value={form.expectedDate} onChange={(e) => setForm((f) => ({ ...f, expectedDate: e.target.value }))} /></div>
                  <div className="form-group"><label>Notes</label><input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" /></div>
                </div>

                {/* Add items */}
                <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "1rem", marginTop: "0.5rem" }}>
                  <label style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.5rem", display: "block" }}>Line Items</label>
                  <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <select value={itemForm.productId} onChange={(e) => setItemForm((f) => ({ ...f, productId: e.target.value }))} style={{ width: "100%", padding: "0.5rem", border: "1px solid #d1d5db", borderRadius: "4px" }}>
                        <option value="">Select product...</option>
                        {products.filter((p: any) => p.status === "active").map((p) => <option key={p.productId} value={p.productId}>{p.name} ({p.sku || "no SKU"})</option>)}
                      </select>
                    </div>
                    <input type="number" min="1" value={itemForm.expectedQty} onChange={(e) => setItemForm((f) => ({ ...f, expectedQty: e.target.value }))} placeholder="Qty" style={{ width: "60px", padding: "0.5rem", border: "1px solid #d1d5db", borderRadius: "4px" }} />
                    <input type="number" step="0.01" value={itemForm.unitCost} onChange={(e) => setItemForm((f) => ({ ...f, unitCost: e.target.value }))} placeholder="Cost $" style={{ width: "80px", padding: "0.5rem", border: "1px solid #d1d5db", borderRadius: "4px" }} />
                    <button type="button" onClick={addItem} style={{ padding: "0.5rem 0.75rem", background: "#2563eb", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>+</button>
                  </div>

                  {form.items.length > 0 && (
                    <div style={{ border: "1px solid #e5e7eb", borderRadius: "6px", overflow: "hidden" }}>
                      {form.items.map((item: any, idx: number) => (
                        <div key={idx} style={{ display: "flex", alignItems: "center", padding: "0.5rem 0.75rem", borderBottom: "1px solid #f3f4f6", fontSize: "0.85rem" }}>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontWeight: 500 }}>{item.productName}</span>
                            {item.requiresSerialNumber && <span style={{ marginLeft: "0.5rem", color: "#7c3aed", fontSize: "0.75rem" }}>SN Required</span>}
                          </div>
                          <span style={{ color: "#6b7280" }}>×{item.expectedQty} @ ${item.unitCost.toFixed(2)}</span>
                          <button type="button" onClick={() => removeItem(idx)} style={{ marginLeft: "0.5rem", background: "none", border: "none", color: "#dc2626", cursor: "pointer" }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ width: "auto" }} disabled={form.items.length === 0}>Create PO</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
